import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Input } from '@/components/ui-kit/input';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { CheckboxList } from '@/components/core/checkbox-list/checkbox-list';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useCreateHoliday, useUpdateHoliday } from '../../hooks/use-holidays';
import { useGetHolidayCantons } from '../../hooks/use-holiday-lookups';
import { holidayFormSchema, HolidayFormValues } from '../../schemas/holiday-form-schema';
import { Holiday } from '../../types/holiday.types';
import { HolidayCantonOption } from '../../services/holiday-lookups.service';
import { holidayDay } from '../../utils/holiday-days';
import { toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';

export interface HolidayFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  holiday?: Holiday | null;
}

export const HolidayFormDialog = ({ open, onOpenChange, holiday }: HolidayFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(holiday);

  const { mutateAsync: createHoliday, isPending: isCreating } = useCreateHoliday();
  const { mutateAsync: updateHoliday, isPending: isUpdating } = useUpdateHoliday();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<HolidayFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(holidayFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Name: '', HolidayDate: '', IsRegional: false, LocationIds: [] },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      Name: holiday?.Name ?? '',
      HolidayDate: holiday ? holidayDay(holiday) : '',
      IsRegional: Boolean(holiday?.IsRegional),
      LocationIds: holiday?.CantonIds ?? [],
    });
  }, [open, holiday, form]);

  // Only roles holding this permission can create canton-based holidays or change which cantons a holiday
  // applies to; the workflows check it too. Everyone else keeps whatever the holiday already has: the
  // form is filled from it and sent back unchanged (for a holiday with no cantons that is just a name and
  // a date).
  const canEditCantons = useHasPermission(HRM_PERMISSIONS.SETTINGS.HOLIDAYS_MARK_CANTON_BASED);
  const isCantonBased = form.watch('IsRegional');
  const showCantonList = canEditCantons && isCantonBased;
  const { data: cantons, isLoading: isLoadingCantons } = useGetHolidayCantons(showCantonList);

  /** An update replaces the holiday's locations, so they are always sent. */
  const handleSubmit = async (values: HolidayFormValues) => {
    let cantonIds = values.IsRegional ? values.LocationIds : [];

    // A canton that no longer exists would make the save fail, and the picker can't show it to be
    // unticked, so it is dropped here. A selection left empty by that isn't "nationwide".
    if (showCantonList && cantons) {
      const known = new Set(cantons.map((canton) => canton.ItemId));
      cantonIds = cantonIds.filter((id) => known.has(id));
      if (values.IsRegional && cantonIds.length === 0) {
        form.setError('LocationIds', { message: t('REQUIRED_FIELD') });
        return;
      }
    }

    const input = {
      Name: values.Name.trim(),
      // Date only: the picked day at 00:00:00 UTC, stored as it is.
      StartDate: toDateOnlyValue(values.HolidayDate) ?? '',
      IsRegional: values.IsRegional,
      CantonIds: cantonIds,
      Type: 'FullDay',
      IsFirstHalf: false,
    };

    try {
      if (isEditMode && holiday) {
        await updateHoliday({ itemId: holiday.ItemId, input });
      } else {
        await createHoliday(input);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_HOLIDAY') : t('CREATE_HOLIDAY')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('HOLIDAY_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('HOLIDAY_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="HolidayDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DATE')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {canEditCantons && (
              <FormField
                control={form.control}
                name="IsRegional"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0">{t('CANTON_BASED_HOLIDAY')}</FormLabel>
                  </FormItem>
                )}
              />
            )}

            {showCantonList && (
              <FormField
                control={form.control}
                name="LocationIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('CANTONS')}</FormLabel>
                    <FormControl>
                      <CheckboxList
                        options={(cantons ?? []).map((canton: HolidayCantonOption) => ({
                          value: canton.ItemId,
                          label: canton.State,
                        }))}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoadingCantons}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
